import React, { useEffect } from "react";
import { Provider, useDispatch } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { HashRouter as Router, Switch, Redirect, useHistory } from "react-router-dom";
import "moment/locale/es";
import { Route } from "components";
import { Socket, SocketEvents, Globals, Constants } from 'utils';
import { AuthService } from 'services';
import { store, persistor } from "./store";

// Public
import Home from 'screens/home';

// Auth
import Login from "screens/auth/login/login";
import Register from "screens/auth/register/register";
import RegisterForm from "screens/auth/register/register-form";
import Recover from "screens/auth/recover/recover";
import Reset from "screens/auth/recover/reset";
import Profile from "screens/profile/profile";
import SuccessPassword from "screens/success-password";

// Client
import Drivers from "screens/client/drivers/drivers";
import DriverInvitations from "screens/client/drivers/invitations/invitations";
import Assistants from "screens/client/assistants/assistants";
import AssistantCreate from "screens/client/assistants/create";
import Vehicles from "screens/client/vehicles/vehicles";
import CreateVehicle from "screens/client/vehicles/create";
import Moderators from "screens/client/moderators/moderators";
import CreateModerator from "screens/client/moderators/create";
import UpdateDriver from "screens/client/drivers/update";

// Public
import PublicInvitation from "screens/public/invitations/invitations";

// Scripts
import "jquery/dist/jquery.min.js";
import "bootstrap/dist/js/bootstrap.min.js";

// Styles
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import "./scss/main.scss";

const Container = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    const getUser = async () => {
      const user = store.getState().user;
      if (user) {
        const res: any = await AuthService.checkUser({
          user_id: user?.id,
          withoutLoading: true
        });
        if (res.user) {
          dispatch({
            type: 'SET_USER',
            payload: res.user
          });

          if (!res.user?.register_finished && res.user?.level_id == Constants.LEVELS.CLIENT) {
            if (res.user?.user_type_id == Constants.USER.USER_TYPE.NATURAL) {
              history.replace('/register/natural');
            }
            else {
              history.replace('/register/business');
            }
          }

          if (!res.user?.register_finished && res.user?.level_id == Constants.LEVELS.DRIVER) {
            history.replace('/driver-finish-register');
          }
        }
        else {
          Globals.showError("Lo sentimos, su usuario ha sido eliminado");
          close();
        }
      }
    }
    getUser();

    const close = async () => {
      await dispatch({ type: 'SET_USER', payload: null });
      await dispatch({ type: 'SET_ACCESS_TOKEN', payload: null }); 
      history.replace('/logout');
    }

    Socket.on(SocketEvents.UPDATE_USER, (data: any) => {
      const user = store.getState().user;
      if (user != null && user?.id === data.user_id) {
        getUser();
      }
    });
  }, []);

  return (
    <React.Fragment>
      <Switch>
        {/* Public */}
        <Route exact path="/" component={Home} />
        <Route exact path="/success-password" component={SuccessPassword} />
        <Route exact noAuth path="/invitation/:code" component={PublicInvitation} />

        {/* Auth */}
        <Route exact noAuth path="/login" component={Login} />
        <Route exact noAuth path="/register" component={Register} />
        <Route exact path="/register/natural" component={RegisterForm} />
        <Route exact path="/register/business" component={RegisterForm} />
        <Route exact noAuth path="/recover" component={Recover} />
        <Route exact noAuth path="/recover/:code" component={Reset} />
        <Route exact auth path="/profile" component={Profile} />
        <Route exact auth withoutFrame path="/driver-finish-register" component={PublicInvitation} />

        {/* Cliente */}
        <Route exact client path="/client/vehicles" component={Vehicles} />
        <Route exact client path="/client/vehicles/create/:id?" component={CreateVehicle} />
        <Route exact client path="/client/assistants" component={Assistants} />
        <Route exact client path="/client/assistants/create" component={AssistantCreate} />
        <Route exact client path="/client/assistants/create/:id?" component={AssistantCreate} />
        <Route exact client path="/client/moderators" component={Moderators} />
        <Route exact client path="/client/moderators/create/:id?" component={CreateModerator} />
        <Route exact client path="/client/drivers" component={Drivers} />
        <Route exact client path="/client/drivers/invitations" component={DriverInvitations} />
        <Route exact client path="/client/drivers/update/:id" component={UpdateDriver} />

        {/* Moderators */}
        <Route exact client_moderator path="/moderators/vehicles" component={Vehicles} />
        <Route exact client_moderator path="/moderators/vehicles/create/:id?" component={CreateVehicle} />
        <Route exact client_moderator path="/moderators/assistants" component={Assistants} />
        <Route exact client_moderator path="/moderators/assistants/create" component={AssistantCreate} />
        <Route exact client_moderator path="/moderators/assistants/create/:id?" component={AssistantCreate} />
        <Route exact client_moderator path="/moderators/drivers" component={Drivers} />
        <Route exact client_moderator path="/moderators/drivers/invitations" component={DriverInvitations} />
        <Route exact client_moderator path="/moderators/drivers/update/:id" component={UpdateDriver} />

        <Redirect to="/" />
      </Switch>
    </React.Fragment>
  )
}

const App = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <Router>
        <Container />
      </Router>
    </PersistGate>
  </Provider>
);

export default App;
